import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ActivityIndicator } from 'components/BlockingLoader/ActivityIndicator';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { Pill } from 'components/Pill';
import { Txt } from 'components/Txt';
import { theme } from 'lib/theme';
import { rbAddReferencePrefix } from 'lib/riskbase';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { selectTaskDetailsState } from 'store/tasks/selectors';
import { TaskDetailsResponse } from 'types/Tasks';
import { useSelector } from 'hooks/useSelector';
import { PageEmpty } from 'components/PageEmpty';
import { GapSmall } from 'components/GapSmall';
import { TaskAssignedComment } from './TaskAssignedComment';
import { pluralise } from 'lib/pluralise';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import {
  getDueDateIconColor,
  getDueDateLabel,
  getStatusIconColor,
} from './utilities';
import { useDispatch } from 'react-redux';
import { doTasksAssignedDetailsClose } from 'store/actions';

const EMPTY_VALUE = 'Not Set';

type DetailProps = NativeStackScreenProps<
  NavigationStacks[Stacks.TasksAssigned],
  Screens.TasksAssignedDetail
>;

function formatCurrency(value?: number | null): string {
  if (value === undefined || value === null) {
    return EMPTY_VALUE;
  }
  return `${(value / 100).toFixed(2)}`;
}

function formatHexColour(colour?: string | null): string | undefined {
  if (!colour) {
    return undefined;
  }
  return colour.startsWith('#') ? colour : `#${colour}`;
}

const TaskDetailsContent: React.FC<{
  task: TaskDetailsResponse;
}> = ({ task }) => {
  const what = task.what || 'No Description';
  const showTitle = !!task.title && task.title !== what;
  const cost = task.cost !== null ? formatCurrency(task.cost) : null;
  const title = task.title.trim();

  return (
    <>
      <Card>
        <CardRow childrenStyle={{ gap: theme.padding }}>
          <View>
            {showTitle ? (
              <>
                <Txt weight="bold" size="large">
                  {title}
                </Txt>
                <Txt>{what}</Txt>
              </>
            ) : (
              <Txt weight="bold" size="large">
                {what}
              </Txt>
            )}
          </View>

          <SeparatorHorizontal />

          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              alignContent: 'center',
              columnGap: theme.padding,
              rowGap: theme.padding / 2,
              flexWrap: 'wrap',
              minHeight: 27,
            }}
          >
            <Txt size="xxxsmall" weight="bold" numberOfLines={1}>
              {rbAddReferencePrefix(task.reference)}
              {/* {!!task.customReference && ` • ${task.customReference}`} */}
            </Txt>
            <Pill
              withIcon={true}
              iconSize={8}
              iconColor={getStatusIconColor(task.statusType)}
              label={task.statusName}
              textSize="xxxsmall"
              textWeight="bold"
            />
            <Pill
              withIcon={true}
              iconSize={8}
              iconColor={getDueDateIconColor(task.dueDate)}
              label={getDueDateLabel(task.dueDate)}
              textSize="xxxsmall"
              textWeight="bold"
            />
          </View>
        </CardRow>

        {!!task.why && <CardRow label="Why" labelAlignTop value={task.why} />}
        {!!task.where && (
          <CardRow
            label="Where"
            labelAlignTop
            value={task.where || EMPTY_VALUE}
          />
        )}
      </Card>

      <GapSmall>
        {/** Property */}
        {/* <Card>
          <CardRow childrenStyle={{ gap: theme.padding }}>
            <View style={{ flexDirection: 'row', gap: theme.padding }}>
              <Avatar
                icon={ICONS.HouseBuildingLight}
                data={{
                  localUrl: '',
                }}
                size={40}
              />
              <View>
                <Txt weight="bold">James Manor</Txt>
                <Txt
                  size="xsmall"
                  style={{ color: theme.colors.fieldValueText }}
                >
                  11 Queens Street, Windsor, SL4 1AT
                </Txt>
              </View>
            </View>
          </CardRow>
        </Card> */}

        {/** Asset (Flat Entrance Door, Emergency Call Point, etc) */}
        {/* <Card>
          <CardRow childrenStyle={{ gap: theme.padding }}>
            <View style={{ flexDirection: 'row', gap: theme.padding }}>
              <Avatar
                icon={ICONS.DiceD6Light}
                data={{
                  localUrl: '',
                }}
                size={40}
              />
              <View>
                <Txt weight="bold">Flat Entrance Door</Txt>
                <Txt
                  size="xsmall"
                  style={{ color: theme.colors.fieldValueText }}
                >
                  Floor: 1, Flat Number: 20
                </Txt>
              </View>
            </View>
          </CardRow>
        </Card> */}

        <Card>
          {!!task.timescaleName && (
            <CardRow
              icon={ICONS.ClockLight}
              label="Timescale"
              childrenStyle={styles.priorityPillContainer}
            >
              <Pill
                withIcon
                transparent
                iconColor={formatHexColour(task.timescaleColor)}
                label={task.timescaleName}
              />
            </CardRow>
          )}
          {!!task.severityName && (
            <CardRow
              icon={ICONS.ArrowUpShortWideLight}
              label="Severity"
              childrenStyle={styles.priorityPillContainer}
            >
              <Pill
                withIcon
                transparent
                iconColor={formatHexColour(task.severityColor)}
                label={task.severityName}
              />
            </CardRow>
          )}
          <CardRow
            icon={ICONS.TagLight}
            label="Category"
            value={task.categoryName || EMPTY_VALUE}
          />
          <CardRow
            icon={ICONS.InputNumberDuo}
            label="Quantity"
            value={String(task.quantity)}
          />
          {cost !== null && (
            <CardRow
              icon={ICONS.SterlingSignLight}
              label="Estimated Cost"
              value={cost}
            />
          )}
        </Card>
      </GapSmall>

      <View>
        <Card
          label={pluralise(
            task.comments,
            '{x} Comment',
            '{x} Comments',
            'No Comments',
          )}
        ></Card>
        {task.comments.length ? (
          <GapSmall>
            {task.comments.map((comment) => (
              <TaskAssignedComment
                key={comment.id}
                authorName={comment.author.name}
                authorAvatarUrl={comment.author.avatar}
                createdAt={new Date(comment.createdDate)}
                comment={comment.comment}
              />
            ))}
          </GapSmall>
        ) : (
          <Card>
            <CardRow placeholder="No comments added yet..." />
          </Card>
        )}
      </View>
    </>
  );
};

export const TaskDetailsPanel: React.FC<{
  onPressBackButton?: () => void;
  isMobile: boolean;
}> = ({ onPressBackButton, isMobile }) => {
  const detailsState = useSelector(selectTaskDetailsState);

  const useScrollView =
    !detailsState.error && !detailsState.isLoading && !!detailsState.item;

  return (
    <Page
      scrollView={useScrollView}
      withSizingProvider={isMobile}
      header={
        isMobile && (
          <PageHeader
            title="Task Details"
            onPressBackButton={onPressBackButton}
          />
        )
      }
    >
      {detailsState.error ? (
        // Show error
        <PageEmpty
          icon={ICONS.ExclamationSolid}
          text="Unable to Load Task"
          subtext={detailsState.error}
          style={{ flex: 1, justifyContent: 'center' }}
        />
      ) : detailsState.isLoading ? (
        // Show loader
        <View
          style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}
        >
          <ActivityIndicator size={56} />
        </View>
      ) : !detailsState.item ? (
        // Show no task selected
        <PageEmpty
          text="No Task Selected"
          subtext="Select a task from the list to view its details"
        />
      ) : (
        // Show task details
        <TaskDetailsContent task={detailsState.item} />
      )}
    </Page>
  );
};

export const TasksAssignedDetailScreen: React.FC<DetailProps> = ({
  navigation,
}) => {
  const dispatch = useDispatch();
  React.useEffect(() => {
    return () => {
      // Annoyingly, this will be called in dev when making changes locally because
      // of how hot reloading works. This will mean the opened task's details will be
      // lost on each change – at the moment, we'll need to comment this out when developing
      dispatch(doTasksAssignedDetailsClose());
    };
  }, [dispatch]);
  return (
    <TaskDetailsPanel onPressBackButton={navigation.goBack} isMobile={true} />
  );
};

const styles = StyleSheet.create({
  priorityPillContainer: {
    alignItems: 'flex-end',
    flexShrink: 1,
    marginLeft: theme.padding,
  },
  detailEmptyState: {
    alignItems: 'center',
    flex: 1,
    gap: theme.paddingHorizontalHalf,
    justifyContent: 'center',
    padding: theme.padding * 3,
  },
  detailLoader: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  errorTitle: {
    color: theme.colors.red,
    marginBottom: theme.paddingHorizontalHalf,
  },
  errorText: {
    color: theme.colors.fieldValueText,
  },
});
